<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { type ResizeEmitProps } from '@/components/lib/GwVirtualList/GwVirtualList.vue'
import type { ID } from '@/types/basic'
import GwBox from '@/components/lib/GwBox/GwBox.vue'

export interface GwTabType {
  id: ID
  name: string
}

export interface GwTabProps {
  id?: string
  item: GwTabType
  hasActiveAutoScroll?: boolean
  disabled?: boolean
  active?: boolean
  hasDynamicSizeEmit?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<GwTabProps>(), {
  item: () => ({ id: '', name: '' }),
  direction: 'vertical',
  valueKey: 'id',
  hasActiveAutoScroll: true,
  bindings: () => {},
})

const emit = defineEmits<{
  click: [Event, GwTabType]
  'tab:mousedown': [Event, GwTabType]
  'tab:mouseup': [Event, GwTabType]
  'tab:touchstart': [Event, GwTabType]
  'tab:touchend': [Event, GwTabType]
  'tab:resized': [ResizeEmitProps<GwTabType>]
}>()

const tabRef = ref()

if (props.hasDynamicSizeEmit) {
  const resizeObserver = new ResizeObserver(() => {
    const res: ResizeEmitProps<GwTabType> = {
      el: tabRef.value,
      item: props.item,
    }
    emit('tab:resized', res)
  })
  onMounted(() => {
    resizeObserver.observe(tabRef.value)
  })
  onBeforeUnmount(() => {
    resizeObserver.disconnect()
  })
}
</script>

<template>
  <div
    :id
    ref="tabRef"
    class="tab_wrapper gw_loading"
    :class="active ? 'gw_hvr_content_show' : 'gw_hvr'"
    @click="emit('click', $event, item)"
    @mousedown="emit('tab:mousedown', $event, item)"
    @mouseup="emit('tab:mouseup', $event, item)"
    @touchstart="emit('tab:touchstart', $event, item)"
    @touchend="emit('tab:touchend', $event, item)"
  >
    <GwBox
      class="gwtab gw_hvr"
      :class="{ gw_loading_text: loading }"
      :active
      variant="box_tab"
      :data-tst="`gw_box${active ? '_active' : ''}`"
    >
      <slot :item="item">
        {{ item.name }}
      </slot>
    </GwBox>
  </div>
</template>

<style lang="scss" scoped>
.gwtab {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  white-space: nowrap;
  cursor: pointer;
}
</style>
